import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter, ErrorComponent } from '@tanstack/react-router';
import { Provider, useSetAtom } from 'jotai';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import * as Sentry from '@sentry/react';

import { Toaster } from '@/components/ui/toaster';
import { testModeAtom } from '@/store';
import { useEffect } from 'react';
import { shouldRetryQuery } from '@/lib/query-retry';

// Import i18n initialization
import './i18n';

import { routeTree } from './routeTree.gen';
import './index.css';
import { UserProvider } from './contexts/user.context';

const env = import.meta.env;

const NODE_ENV = env.VITE_NODE_ENV;
// Initialize PostHog (production and staging only)
if (NODE_ENV === 'production' || NODE_ENV === 'staging') {
  posthog?.init(env.VITE_POSTHOG_API_KEY!, {
    api_host: env.VITE_POSTHOG_HOST!,
  });
}
if (NODE_ENV === 'production' || NODE_ENV === 'staging') {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing — sample a fraction of sessions; 1.0 traced/profiled/replayed
    // every prod session (Sentry quota burn, and replays capture user input
    // on a billing product). Error replays stay at 100%.
    tracesSampleRate: 0.2,
    tracePropagationTargets: ['localhost', /^https:\/\/app\.glitchads\.ai/, /^https:\/\/staging\.glitchads\.ai/],
    profilesSampleRate: 0.2,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: NODE_ENV,
  });
}
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: shouldRetryQuery,
    },
  },
});
const GOOGLE_ADS_CLIENT_ID = env.VITE_GOOGLE_ADS_CLIENT_ID!;
const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    impersonate?: { action?: string };
  }
}


// Dev-only bridge for Playwright: exposes a window hook that flips the in-memory test-mode atom,
// for specs that drive test-mode-only surfaces. Lives inside the Jotai <Provider> so it writes
// the same store the app reads; excluded from production bundles by the DEV guard.
function DevTestModeBridge() {
  const setTestMode = useSetAtom(testModeAtom);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __glitchSetTestMode?: (v: boolean) => void }).__glitchSetTestMode = setTestMode;
    return () => {
      delete (window as unknown as { __glitchSetTestMode?: (v: boolean) => void }).__glitchSetTestMode;
    };
  }, [setTestMode]);
  return null;
}

const rootElement = document.getElementById('app')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <Provider>
            <GoogleOAuthProvider clientId={GOOGLE_ADS_CLIENT_ID}>
              <UserProvider>
                <DevTestModeBridge />
                <Toaster />
                <RouterProvider router={router} />
              </UserProvider>
            </GoogleOAuthProvider>
          </Provider>
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>,
  );
}
